import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { MouseEvent } from 'react';
import { Breadcrumb } from '../../../../components/Atoms/BreadCrumb';
import { ButtonVariants } from '../../../../components/Atoms/Button';
import { Loader } from '../../../../components/Atoms/Loader';
import { StickyCTA } from '../../../../components/CTA/StickyCTA';
import { CTA_KEY, FILE_NOW_TEXT } from '../../../../components/CTA/constants';
import { FaqComponent } from '../../../../components/Common/FAQS';
import TableOfContents from '../../../../components/Common/TableOfContents';
import { ContentComponent } from '../../../../components/IfscCode/ContentComponent';
import IndividualsIfsc from '../../../../components/IfscCode/IndividualsIfsc';
import SearchIFSC from '../../../../components/IfscCode/SearchIFSC';
import IFSCDataService from '../../../../components/IfscCode/Services/ifsc.service';
import SmartInvestment from '../../../../components/IfscCode/smartInvestment';
import {
  BankDetail,
  ifscDetailsAPIInterface,
} from '../../../../components/IfscCode/types';
import { ClearInNavbar } from '../../../../components/Navbar/ClearNavbar';
import {
  replaceSpaceWithHyphen,
  toTitleCase,
  urlformatter,
} from '../../../../components/Utils/CommonUtils';
import { TopCTA } from '../../../../components/cms/layout/components';
import {
  NAV_TYPES,
  b2cData,
} from '../../../../components/cms/layout/components/header';
import { BankSpecificContents } from '../../../../constants/IfscConstants';
import { Size } from '../../../../constants/commonEnums';
import {
  faqSchema,
  getBreadCrumbIFSC,
} from '../../../../constants/componentStaticData';
import {
  DEPLOYMENT_DOMAIN,
  ENABLE_IFSC_PAGES,
} from '../../../../env-vars.constants';
import { Meta } from '../../../../layout/Meta';
import { render404Page, returnNoPaths } from '../../../../utils/UtilFunctions';
import { getCachedCTAData } from '../../../../utils/cta-cache';
import { staticPropsWrapper } from '../../../../utils/static-pages/utils';
import { handleTrack } from '../../../../utils/tracker';

type BREADCRUMB_TYPE = {
  title: string | undefined | string[];
  link: string | null;
};

interface ifscDetailsPropsInterface {
  bankDetails: ifscDetailsAPIInterface;
  top_cta: string;
}

const IfscPage = ({
  bankDetails,
  top_cta,
}: ifscDetailsPropsInterface): JSX.Element => {
  const router = useRouter();

  if (router.isFallback) {
    return (
      <div className="loaderParentDiv">
        <Loader size={Size.MEDIUM} />
      </div>
    );
  }

  const {
    bank_name,
    ifsc_code,
    micr_code,
    branch_name,
    city,
    district,
    address,
    state,
    nearby_branches,
  } = bankDetails;

  const BREADCRUMB: Array<BREADCRUMB_TYPE> = [
    { title: 'Home', link: '/' },
    { title: 'IFSC code', link: '/ifsc-code/' },
    {
      title: toTitleCase(bank_name),
      link: '/ifsc-code/' + urlformatter(bank_name) + '/',
    },
    {
      title: `${branch_name.toUpperCase()}, ${bank_name.toUpperCase()}`,
      link: null,
    },
  ];

  const listOfContent = [
    {
      id: 'nearby',
      header: `Nearby ${toTitleCase(bank_name)} branches`,
    },
    {
      id: 'howtofind',
      header: 'How to find IFSC Code?',
    },
    {
      id: 'usesIFSC',
      header: 'Uses of IFSC Code',
    },
    {
      id: 'formatIFSC',
      header: 'Format of IFSC Code',
    },
    {
      id: 'howMICR',
      header: 'How to find MICR Code',
    },
    {
      id: 'faqs',
      header: 'FAQs',
    },
  ];

  const IFSCFAQS: Array<{
    header: string;
    content: React.ReactElement | string;
  }> = [
    {
      header: `What is the IFSC code of ${toTitleCase(
        branch_name
      )}, ${bank_name.toUpperCase()} ?`,
      content: (
        <p>
          The IFSC code of {toTitleCase(branch_name)}, {bank_name.toUpperCase()}{' '}
          is {ifsc_code}
        </p>
      ),
    },
    {
      header: `What is the address associated with ${ifsc_code}`,
      content: (
        <p>
          The address associated with {ifsc_code} is <br /> <br />
          <span className="break-all ">{address}</span>
        </p>
      ),
    },
    {
      header: `Which district falls under the ${bank_name.toUpperCase()} ?`,
      content: (
        <p>
          The district that falls under the {bank_name.toUpperCase()} is{' '}
          {toTitleCase(district)}
        </p>
      ),
    },
  ];

  const URLPath = `${urlformatter(bank_name)}/${urlformatter(
    city
  )}/${urlformatter(branch_name)}/${ifsc_code}`;

  const captureEvent = (e: MouseEvent<Element, MouseEvent>) => {
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };

  return (
    <div>
      <ClearInNavbar data={b2cData} />
      <Meta
        title={`IFSC Code of ${toTitleCase(bank_name)}, ${toTitleCase(
          branch_name
        )}, ${toTitleCase(district)}, ${toTitleCase(
          state
        )} - IFSC, MICR Code, Latest Phone number & Address`}
        description={`IFSC Code of ${toTitleCase(bank_name)}, ${toTitleCase(
          branch_name
        )}  is  ${ifsc_code} and MICR Code is ${micr_code}. Search and Find IFSC Code for NEFT and RTGS Transactions`}
        canonical={`https://cleartax.in/ifsc-code/${URLPath}/`}
        ampURL={`https://cleartax.in/ifsc-code/amp/${URLPath}/`}
      />
      <TopCTA CTA={top_cta} onClickCB={captureEvent} />
      <div className="wg-container lg:hidden xl:hidden md:hidden">
        <div className="mx-auto my-8 sm:mx-0">
          <Breadcrumb dataSet={BREADCRUMB} />
        </div>
        <div className="py-6">
          <SearchIFSC />
        </div>
      </div>
      <div className="wg-container py-12 flex mx-auto text-font-500 sm:flex-col-reverse">
        <div className="w-3/4 pr-12 sm:pr-0 sm:w-full sm:mt-6">
          <div className="sm:hidden">
            <div className="mx-auto my-8">
              <Breadcrumb dataSet={BREADCRUMB} />
            </div>
            <div className="py-12">
              <SearchIFSC />
            </div>
          </div>
          <IndividualsIfsc bankDetails={bankDetails} />
          <SmartInvestment />

          <div className="py-6">
            <h2 className="text-s-40 sm:text-s-20">
              Nearby {bank_name.toUpperCase()} branches
            </h2>
            <span className="anchor" id="nearby"></span>
            <div className="flex flex-wrap items-center py-4">
              {nearby_branches && nearby_branches.length == 0 && (
                <p className="mt-4">NO NEAR BY BRANCHES FOUND</p>
              )}
              {nearby_branches &&
                nearby_branches.length > 0 &&
                nearby_branches.map((ele, index) => {
                  return (
                    <div className="pr-6 w-1/4 sm:w-1/2 mt-5" key={index}>
                      <a
                        className="cursor-pointer"
                        onClick={() => {
                          const targetUrl = `/ifsc-code/${replaceSpaceWithHyphen(
                            ele.bank_name
                          )}/${replaceSpaceWithHyphen(
                            ele.city
                          )}/${replaceSpaceWithHyphen(ele.branch_name)}/${
                            ele.ifsc_code
                          }`;
                          if (router.asPath !== targetUrl) {
                            router.push(targetUrl);
                          }
                        }}
                      >
                        <div className="py-5 px-6 w-full text-center rounded-xl bg-blue-50 text-base text-font-500">
                          {ele.branch_name.length < 18
                            ? toTitleCase(ele.branch_name)
                            : toTitleCase(ele.branch_name).slice(0, 16) + '..'}
                        </div>
                      </a>
                    </div>
                  );
                })}
            </div>
            <p className="py-4">
              IFSC code is allotted by the Reserve Bank of India (RBI) to all
              banks and its branches. The IFSC code of a bank can be commonly
              found on a bank account passbook, cheque leaf issued by the bank
              or on the RBI website. Any kind of fund transfer through a bank
              requires a valid IFSC. The different types of fund transfers are
              NEFT, RTGS and IMPS.
            </p>
          </div>

          {BankSpecificContents.map((content, index: number) => (
            <ContentComponent
              key={'contents' + index}
              id={content?.id}
              header={content?.header}
              para1={content?.para1}
              list={content?.list}
              table={content?.table}
              image={content?.image}
            />
          ))}

          <div className="py-6">
            <span className="anchor" id="faqs"></span>
            <FaqComponent
              heading={`FAQs on ${bank_name.toUpperCase()}, ${toTitleCase(
                branch_name
              )},
              ${toTitleCase(city)}, ${toTitleCase(state)}`}
              FAQS={IFSCFAQS}
            />
          </div>
        </div>

        <div className="w-1/4 pl-12 text-font-500 sm:pl-0 sm:w-full right-articles-section">
          <TableOfContents data={listOfContent} />
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getBreadCrumbIFSC(bankDetails)),
        }}
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema(IFSCFAQS)),
        }}
      ></script>

      <StickyCTA
        buttonText={FILE_NOW_TEXT}
        buttonStyle={ButtonVariants.bluefilled}
        type={NAV_TYPES.B2C}
      />
    </div>
  );
};

export default IfscPage;

export const getStaticPaths: GetStaticPaths = async () => {
  if (DEPLOYMENT_DOMAIN !== 'cleartax.in' || ENABLE_IFSC_PAGES !== 'true') {
    return returnNoPaths();
  }
  // bank_name, city, branch_name, ifsc_code
  try {
    const response = await IFSCDataService.getAllBankDetails();
    if (response.status !== 200 || !response.data) {
      return returnNoPaths();
    }
    let END_INDEX = 0;
    if (
      Number(process.env.IFSC_BUILD_LAST_INDEX) === 0 ||
      Number(process.env.IFSC_BUILD_LAST_INDEX) > response.data.length
    ) {
      END_INDEX = response.data.length - 1;
    } else {
      END_INDEX = Number(process.env.IFSC_BUILD_LAST_INDEX);
    }

    const paths = response?.data
      .slice(Number(process.env.IFSC_BUILD_START_INDEX), END_INDEX)
      .map((bankDetail: BankDetail) => {
        return {
          params: {
            bankIndividuals: urlformatter(bankDetail.bank_slug),
            ifsc: [
              urlformatter(bankDetail.city),
              urlformatter(bankDetail.branch_name),
              urlformatter(bankDetail.ifsc_code),
            ] as string[],
          },
        };
      });

    return {
      paths,
      fallback: false,
    };
  } catch (error: unknown) {
    console.error(
      'Error generating static paths:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    return returnNoPaths();
  }
};

export const getStaticProps: GetStaticProps = staticPropsWrapper(
  __filename,
  async (context: {
    params?: { bankIndividuals?: string; ifsc?: string[] };
  }) => {
    const { params } = context;
    if (params?.ifsc?.length != 3) {
      return render404Page();
    }

    if (params?.ifsc?.[2]) {
      try {
        const res = await IFSCDataService.getIFSCDetails(params.ifsc[2]);
        let responseData;
        if (res.status === 200) {
          responseData = await res.data;
        }
        // Use cached CTA data to avoid repeated API calls during build
        const cta = await getCachedCTAData(CTA_KEY.IFSC_CTA);

        return {
          props: {
            bankDetails: responseData,
            top_cta: cta?.cta_html ?? '',
          },
        };
      } catch (error: unknown) {
        console.log(
          error instanceof Error ? error.message : 'Unknown error',
          'error'
        );
        return render404Page();
      }
    } else {
      return render404Page();
    }
  }
);
